import { useIntl } from '@capsicum/common';
import { Button, TextButton } from '@capsicum/common-web';
import { useVerifyModal } from '@capsicum/features/nc-onboarding/shared';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useStepVerifyIcon } from '../../hooks/onboarding/useStepVerifyIcon';

type VerifyModalProps = {
	close: () => void;
};

export const VerifyModal = ({ close }: VerifyModalProps) => {
	const { $t } = useIntl();
	const {
		action,
		buttonText,
		contactUsHereLink,
		descriptionText,
		isStepDisabled,
		shouldShowDescription,
		shouldShowStatus,
		stepsAreCompleted,
		userIsBlocked,
		verificationSteps
	} = useVerifyModal();
	const { getStepVerifyIcon } = useStepVerifyIcon();

	const onClick = () => action(close);
	const openLink = () => window.open(contactUsHereLink, '_blank');

	useEffect(() => {
		if (stepsAreCompleted) {
			close();
		}
	}, [stepsAreCompleted]);

	return (
		<div>
			<S.Wrapper>
				<S.Title>{$t('ncOnboard.verify.title')}</S.Title>
				<S.Description>{descriptionText}</S.Description>
				<S.StepsWrapper>
					{verificationSteps.map(({ description, level, status, title }, index) => (
						<S.Step $disabled={isStepDisabled(level)} key={level}>
							<S.StepIndex>
								{getStepVerifyIcon(level)}
								{index < verificationSteps.length - 1 && <S.Separator />}
							</S.StepIndex>
							<S.StepContent>
								<S.StepTitle>{title}</S.StepTitle>
								{shouldShowStatus(level) && <S.StepStatus>{status}</S.StepStatus>}
								{shouldShowDescription(level) && <S.StepDescription>{description}</S.StepDescription>}
							</S.StepContent>
						</S.Step>
					))}
				</S.StepsWrapper>
			</S.Wrapper>

			<S.ButtonWrapper>
				{!userIsBlocked && (
					<Button fullWidth testID="ncOnboard-verify-button" text={buttonText} onClick={onClick} />
				)}
				<TextButton
					color="neutral"
					testID="ncOnboard-verify-contact-support-button"
					text={$t('ncOnboarding.notSupported.contactSupportButton')}
					onClick={openLink}
				/>
			</S.ButtonWrapper>
		</div>
	);
};

namespace S {
	export const Wrapper = styled.div`
		height: 100%;
		width: 100%;
	`;

	export const Title = styled.p`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title6};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		text-align: center;
	`;

	export const Description = styled.p`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		${({ theme }) => theme.fonts.text.text3};
		margin: ${({ theme }) => theme.spacings.xsmall} 0;
		text-align: center;
	`;

	export const StepsWrapper = styled.div`
		margin: ${({ theme }) => theme.spacings.medium} ${({ theme }) => theme.spacings.small};
	`;

	export const StepIndex = styled.div`
		position: relative;
	`;

	export const Step = styled.div<{ $disabled: boolean }>`
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		${({ $disabled }) => ($disabled ? 'opacity: 0.6' : '')}
	`;

	export const Separator = styled.div`
		border-left: 1px solid ${({ theme }) => theme.colors.borders.main.divider};
		height: calc(100% - ${({ theme }) => theme.spacings.large});
		left: ${({ theme }) => theme.spacings.xsmall};
		position: absolute;
		top: ${({ theme }) => theme.spacings.large};
	`;

	export const StepContent = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.medium};
		padding-left: ${({ theme }) => theme.spacings.small};
	`;

	export const StepTitle = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text1};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const StepDescription = styled.div`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		${({ theme }) => theme.fonts.text.text5};
	`;

	export const StepStatus = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text5};
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const ButtonWrapper = styled.div`
		align-items: center;
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
		justify-content: center;
		margin-top: ${({ theme }) => theme.spacings.large};
	`;
}
